import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditContext';

// Define the window interface to include BIZAI_AGENT_CONTEXT
declare global {
    interface Window {
        BIZAI_AGENT_CONTEXT: any;
    }
}

const PAGE_CONTEXTS: Record<string, string> = {
    '/dashboard': 'User Dashboard - Overview of account, credits, and recent activities.',
    '/image-enhancement': 'Image Enhancement Service - AI tool to enhance real estate photos (HDR, brightness, clarity).',
    '/interior-design': 'Interior Design Service - Virtual staging and interior redesign using AI.',
    '/replace-elements': 'Replace Elements Service - Replace specific objects or areas in an image.',
    '/add-furnitures': 'Add Furniture Service - Virtually furnish empty rooms with realistic furniture.',
    '/exterior-design': 'Exterior Design Service - Renovate and redesign building exteriors/facades.',
    '/smart-effects': 'Smart Effects Service - Apply weather effects like day-to-dusk, twilight, or sunny conversions.',
    '/users': 'User Management (Admin) - Manage registered users and their permissions.',
    '/settings': 'Account Settings - Manage profile, password, and subscription plan.',
    '/pricing': 'Pricing & Plans - View subscription plans and purchase credit packages.',
    '/auth': 'Authentication - Login, Sign up, or Verify OTP.',
    '/payment-success': 'Payment Success - Confirmation page after successful payment.',
    '/privacy': 'Privacy Policy - Information about data handling.',
    '/': 'Landing Page - Home page of RealVisionAI.'
};

const BizAIIntegration: React.FC = () => {
    const location = useLocation();
    const { user } = useAuth();
    const { creditBalance } = useCredits();
    const loadedRef = useRef(false);

    // Update bot context on route or user change
    useEffect(() => {
        const path = location.pathname;
        let pageDescription = PAGE_CONTEXTS[path];

        // Handle Admin sub-routes
        if (!pageDescription && path.startsWith('/admin')) {
            pageDescription = 'Admin Dashboard - Administrative controls.';
        }

        // Fallback for unknown routes
        if (!pageDescription) {
            pageDescription = `Current Page: ${path} - RealVisionAI Application`;
        }

        // Handle Guest ID consistency
        let guestId = localStorage.getItem('bizai_guest_id');
        if (!guestId) {
            guestId = 'guest_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('bizai_guest_id', guestId);
        }
        const currentUserId = user?.id || guestId;

        // Prepare context
        const context = {
            page: path,
            pageTitle: document.title,
            description: pageDescription, // Keeping description for better context understanding
            user_id: currentUserId,
            user_email: user?.email || 'guest',
            user_role: user?.role || 'visitor',
            plan: user?.subscription_plan || 'none',
            credits_available: creditBalance?.displayCreditsRemaining ?? 0,
            credits_used: creditBalance?.displayCreditsUsed ?? 0,
            credits_total: creditBalance?.displayCreditsTotal ?? 0
        };

        // Set global context variable (Method 1)
        window.BIZAI_AGENT_CONTEXT = context;

    }, [location.pathname, user?.id, user?.email, user?.role, user?.subscription_plan, creditBalance]);

    // Load the script once
    useEffect(() => {
        if (loadedRef.current) return;

        const scriptId = 'bizai-script';
        if (!document.getElementById(scriptId)) {
            // Cleanup old script if exists
            const oldScript = document.getElementById('lolabot-script');
            if (oldScript) oldScript.remove();

            // Cleanup existing instances to prevent duplicates (migrated from previous file)
            const iframes = document.querySelectorAll('iframe');
            iframes.forEach(iframe => {
                if (iframe.src && (iframe.src.includes('/chat/lola-demo') || iframe.src.includes('easypanel.host'))) {
                    iframe.remove();
                }
            });

            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://app.bizaigpt.com/embed/bizai-agent.js';
            script.async = true;
            script.setAttribute('data-bot-id', 'real-vision-ai');
            script.setAttribute('data-theme', 'dark');
            document.body.appendChild(script);

            loadedRef.current = true;
        }
    }, []);

    return null;
};

export default BizAIIntegration;
